import type { MenuLegalMark } from '@web-studio/shared-schemas';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveLucideByIconKey } from '../../icons/menuTagLucideMap';
import {
  collectMenuLegalLegendDietary,
  collectMenuLegalLegendMarks,
  type MenuLegendDietaryEntry,
} from '../../lib/collectMenuLegalLegend';
import type { MenuColumn } from '../../lib/parseMenuColumns';
import { pickChrome } from '../../lib/siteChromeCopy';

export {
  collectMenuLegalLegendDietary,
  collectMenuLegalLegendMarks,
} from '../../lib/collectMenuLegalLegend';
export type { MenuLegendDietaryEntry } from '../../lib/collectMenuLegalLegend';

function LegendGroup({
  title,
  marks,
}: {
  title: string;
  marks: MenuLegalMark[];
}) {
  if (marks.length === 0) {
    return null;
  }
  return (
    <div className="ws-menu-legend__group">
      <p className="ws-menu-legend__group-title">{title}</p>
      <dl className="ws-menu-legend__list">
        {marks.map((mark) => (
          <div key={`${mark.kind}-${mark.code}`} className="ws-menu-legend__row">
            <dt className="ws-menu-legend__code">{mark.code}</dt>
            <dd className="ws-menu-legend__label">{mark.label}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function DietaryLegendGroup({
  title,
  entries,
}: {
  title: string;
  entries: MenuLegendDietaryEntry[];
}) {
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="ws-menu-legend__group">
      <p className="ws-menu-legend__group-title">{title}</p>
      <dl className="ws-menu-legend__list">
        {entries.map((entry) => {
          const Icon = resolveLucideByIconKey(entry.iconKey);
          return (
            <div key={entry.id} className="ws-menu-legend__row">
              <dt className="ws-menu-legend__code ws-menu-legend__code--icon">
                {Icon ? (
                  <LucideIcon
                    icon={Icon}
                    size="sm"
                    strokeWidth={2.25}
                    className="ws-menu-legend__dietary-icon"
                  />
                ) : (
                  <span aria-hidden="true">·</span>
                )}
              </dt>
              <dd className="ws-menu-legend__label">{entry.label}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

/**
 * Collapsed-by-default Kennzeichnung footnote under a menu block.
 * Allergens, additives, and dietary claims used on dishes (not the full registry).
 * Uses native `<details>` so SSR / edge HTML stay interactive without JS.
 */
export function MenuLegalLegend({
  columns,
  locale,
}: {
  columns: readonly MenuColumn[];
  locale: string;
}) {
  const marks = collectMenuLegalLegendMarks(columns, locale);
  const dietary = collectMenuLegalLegendDietary(columns, locale);
  if (marks.length === 0 && dietary.length === 0) {
    return null;
  }
  const allergens = marks.filter((mark) => mark.kind === 'allergen');
  const additives = marks.filter((mark) => mark.kind === 'additive');

  return (
    <details className="ws-menu-legend">
      <summary className="ws-menu-legend__summary">
        <span className="ws-menu-legend__summary-label">
          {pickChrome('menuLegalLegend', locale)}
        </span>
      </summary>
      <div className="ws-menu-legend__body">
        <LegendGroup title={pickChrome('menuLegalAllergens', locale)} marks={allergens} />
        <LegendGroup title={pickChrome('menuLegalAdditives', locale)} marks={additives} />
        <DietaryLegendGroup
          title={pickChrome('menuLegalDietary', locale)}
          entries={dietary}
        />
      </div>
    </details>
  );
}
